import React from 'react'
// libs
import styled from 'styled-components'
import { useQuery } from '@apollo/client/react'
import { useRouter } from 'hooks/useRouter'
import { Droppable, Draggable } from 'react-beautiful-dnd'
// components
import {
  DropContainer,
  Accordion,
  AccordionDetails,
  AccordionSummary
} from 'components/PageComponents/Settings/ObjectsAndProperties/Edit/Properties/components/view'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { PopoverModal } from 'components/UI'
import { Button } from 'components/PageComponents/Home/components/DashboardWidgets/components/StrategyActions/components/Content/components/FiltersPanel/components/Menu'
// view components
import PlanTypesFilter from './components/Filter'
import Property from 'components/PageComponents/Settings/ObjectsAndProperties/Edit/Properties/components/Property'
// themes
import { colors } from 'themes'
import MoreIcon from '@mui/icons-material/ArrowDropDown'
// constants
import { PROPERTIES_LISTS } from 'constants/objects'
// context
import { ObjectsBLContext } from 'containers/ObjectsAndProperties/Edit/layers/BL'
// graphql
import { PLAN_TYPES_DETAILS_QUERY, PLAN_TYPES_LIST_QUERY } from 'graphql/planType/queries'
// utils
import get from 'lodash/get'

const Wrapper = styled('div')`
  width: 30%;

  & > button {
    font-size: 15px;
    font-weight: bold;
    height: 21px;
    align-items: center;
    padding: 0;
  }

  .MuiAccordionSummary-content {
    margin: 0;
  }

  .MuiAccordion-root {
    margin-top: 19px !important;
  }
`

const MODAL_PROPS = {
  disableRestoreFocus: true,
  className: null,
  onClose: null,
  PaperProps: {
    style: {
      overflow: 'hidden',
      maxHeight: '500px',
      maxWidth: '285px'
    }
  }
}

const PlanTypeProperties: React.FunctionComponent<{ searchedPlanType: string }> = ({ searchedPlanType }) => {
  const router = useRouter()

  const { data: planTypesQuery } = useQuery(PLAN_TYPES_LIST_QUERY, {
    fetchPolicy: 'network-only',
    variables: {
      filter: {
        description: searchedPlanType
      }
    }
  })
  const { data: planTypeQuery } = useQuery(PLAN_TYPES_DETAILS_QUERY, {
    skip: !router.query.planType,
    fetchPolicy: 'network-only',
    variables: {
      filter: {
        ids: [router.query.planType]
      }
    }
  })

  const [isOpen, setIsOpen] = React.useState<boolean>(true)

  const { selectedPlanType, planTypeProperties, isDropDisabled, handleSelectPlanType } =
    React.useContext(ObjectsBLContext)

  const expandPanel = (_, expanded) => setIsOpen(expanded)

  const planTypes = get(planTypesQuery, 'planTypes.data', []).map((planType) => ({
    ...planType,
    label: planType.description
  }))

  const planType = get(planTypeQuery, 'planTypes.data[0]')

  React.useEffect(() => {
    if (planType) {
      handleSelectPlanType(planType)
    }
  }, [planType])

  return (
    <Wrapper>
      <PopoverModal
        {...MODAL_PROPS}
        trigger={
          <Button>
            {get(selectedPlanType, 'description') || 'Plan Types'}
            <MoreIcon color={'inherit'} fontSize={'large'} />
          </Button>
        }>
        {({ closePopup }) => <PlanTypesFilter closePopup={closePopup} planTypes={planTypes} />}
      </PopoverModal>

      {selectedPlanType && (
        <Accordion expanded={isOpen} onChange={expandPanel}>
          <AccordionSummary expandIcon={<ExpandMoreIcon htmlColor={colors.blue500} />}>
            <div>{`About this plan type`}</div>
          </AccordionSummary>
          <AccordionDetails>
            <Droppable droppableId={PROPERTIES_LISTS.PLAN_TYPE} isDropDisabled={isDropDisabled.PLAN}>
              {(provided) => (
                <DropContainer ref={provided.innerRef}>
                  {planTypeProperties.map((item, index) => (
                    <Draggable key={item.id} draggableId={item.id} index={index}>
                      {(provided) => (
                        <Property
                          innerRef={provided.innerRef}
                          property={item}
                          isPlanType
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                        />
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </DropContainer>
              )}
            </Droppable>
          </AccordionDetails>
        </Accordion>
      )}
    </Wrapper>
  )
}

export default PlanTypeProperties
